import { useCallback, useEffect, useState } from 'react';
import { useSidebarExtension } from '../../context/SidebarExtensionProvider/useSidebarExtension';
import { CaretRight } from 'phosphor-react';
import { Box, Typography } from '@mui/material';
import { Steps } from '../../components/Steps';
import { TaskService } from '../../Service/TaskService';
import { useAppDispatch } from '../../store/hooks';
import { createNotification } from '../../store/notifications/notification-slice';
import { useOutletContext } from 'react-router-dom';
import { QuestionType } from '../../Utilis/Types/Task';
import { randomUUID } from 'crypto';
import { InputType } from '../../components/UI/DynamicInput/types';
import { format } from 'date-fns';

const formatQuestionTitle = (
  questionName: string,
  groupName: string,
  duplicable: boolean,
  index: number
) => {
  if (!!duplicable) {
    return !!groupName
      ? `${groupName} #${index + 1} ${questionName}`
      : `${questionName} #${index + 1}`;
  } else {
    return questionName;
  }
};

const AdminGeneral = () => {
  const [clientDetails] = useOutletContext<any>();
  const { openSidebarExtension, closeSidebarExtension } = useSidebarExtension();
  const appDispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [allTasks, setAllTasks] = useState<any[]>([]);
  const [task, setTask] = useState<any>(null);

  useEffect(() => {
    getTasks();
  }, []);

  useEffect(() => {
    if (!!selectedTask) {
      getSelectedTask();
    }
  }, [selectedTask]);

  useEffect(() => {
    const tabs = allTasks.map(task => ({
      title: task.name,
      endIcon: <CaretRight />,
      selected: selectedTask === task.id,
      onClick: () => setSelectedTask(task.id),
    }));

    openSidebarExtension(tabs);
  }, [selectedTask]);

  const getTasks = async () => {
    setIsLoading(true);
    try {
      const { data } = await TaskService.getTasks();

      setAllTasks(data);
      setSelectedTask(data[0].id);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedTask = async () => {
    setIsLoading(true);
    try {
      const { data } = await TaskService.getAdminSingleTask(
        selectedTask!,
        clientDetails.id
      );

      for (const step of data.steps) {
        const { questions } = step;

        const allQuestions = [];

        allQuestions.push(...step.questions);

        for (const group of step.questionsGroups) {
          allQuestions.push(...group.questions);
        }

        for (const question of allQuestions) {
          const subQuestions = [];
          const { answer } = question.answer || {};

          if (question.type === QuestionType.ADDRESS) {
            const addressFields = ['streetAddress', 'city', 'state', 'zip'];

            const items = addressFields.map((field, index) => {
              const title = field
                .split(/(?=[A-Z])/)
                .map(word => word[0].toUpperCase() + word.substring(1))
                .join(' ');
              return {
                id: String(index),
                title,
                answer: {
                  id: String(index),
                  answer: answer?.[field],
                },
              };
            });

            subQuestions.push(...items);
          } else if (question.type === QuestionType.SSN_EIN) {
            const type = answer?.type || InputType.SSN;
            const fields =
              type === InputType.SSN
                ? ['ssn']
                : ['ein', 'financialInstitution'];

            const items = fields.map((field, index) => {
              const title = field
                .split(/(?=[A-Z])/)
                .map(word => word[0].toUpperCase() + word.substring(1))
                .join(' ');
              return {
                id: String(index),
                title,
                answer: {
                  id: String(index),
                  answer: answer?.[field],
                },
              };
            });

            subQuestions.push(...items);
          }

          if (!!subQuestions.length) {
            question.subQuestions = subQuestions;
          }
        }
      }

      setTask(data);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderInformation = useCallback(
    (question: any) => {
      let { answer } = question.answer || {};

      if (answer !== undefined) {
        if (question.type === QuestionType.BOOLEAN) {
          if (answer === true) {
            answer = 'Yes';
          } else if (answer === false) {
            answer = 'No';
          }
        } else if (question.type === QuestionType.VALUE) {
          answer = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            currencyDisplay: 'narrowSymbol',
          }).format(answer);
        } else if (question.type === QuestionType.MULTISELECT) {
          answer = answer.join(', ');
        } else if (question.type === QuestionType.DATE) {
          answer = format(new Date(answer), 'MM/dd/yyyy');
        }
      }

      return <Typography sx={{ marginTop: '8px' }}>{answer || '-'}</Typography>;
    },
    [task]
  );

  const QuestionDetails = ({
    question,
    group,
  }: {
    question: any;
    group?: any;
  }) => {
    let title = question.title;

    if (!!group) {
      title = formatQuestionTitle(
        question.title,
        group.name,
        group.duplicable,
        group.index
      );
    }

    if (!question.subQuestions) {
      return (
        <Box
          key={question.id}
          sx={{
            flexGrow: 1,
            padding: '16px 0',
            paddingRight: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid #E3DDE6',
          }}
        >
          <Typography
            sx={{
              color: '#655C68',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            {title}
          </Typography>
          {renderInformation(question)}
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
            position: 'relative',
            paddingTop: '15px',
          }}
        >
          <Typography
            sx={{
              color: '#655C68',
              fontSize: '10px',
              position: 'absolute',
              top: '5px',
            }}
          >
            {title}
          </Typography>
          {question.subQuestions.map((subQuestion: any) => {
            return (
              <Box
                key={subQuestion.id}
                sx={{
                  flexGrow: 1,
                  padding: '16px 0',
                  paddingRight: '16px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid #E3DDE6',
                }}
              >
                <Typography
                  sx={{
                    color: '#655C68',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {subQuestion.title}
                </Typography>
                {renderInformation(subQuestion)}
              </Box>
            );
          })}
        </Box>
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '36px',
        padding: '36px 45px',
      }}
    >
      <Steps
        items={[
          clientDetails?.name + ' ' + clientDetails?.lastName,
          'General Information',
        ]}
      />
      {!!task && (
        <>
          <Typography sx={{ fontSize: '20px', fontWeight: '500' }}>
            {task.name}
          </Typography>
          {task.steps.map((step: any) => {
            return (
              <Box>
                <Typography
                  sx={{
                    color: '#655C68',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  {step.name}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'end',
                    border: '1px solid #E3DDE6',
                    background: 'white',
                    borderRadius: '4px',
                    boxShadow: '0px 2px 8px 0px #00000014',
                    padding: '0 16px',
                    paddingBottom: '0',
                  }}
                >
                  {step.questions.map((question: any) => (
                    <QuestionDetails question={question} />
                  ))}
                  {step.questionsGroups.map(
                    (group: any, groupIndex: number) => {
                      group.index = groupIndex;
                      return (
                        <Box
                          key={group.id}
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            width: '100%',
                          }}
                        >
                          {group.questions.map((question: any) => {
                            return (
                              <QuestionDetails
                                question={question}
                                group={group}
                              />
                            );
                          })}
                        </Box>
                      );
                    }
                  )}
                </Box>
              </Box>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default AdminGeneral;
